import type { TagColorMap, Task } from '@portfolio-pm/shared';
import { parseRecurrence, parseSmartAdd } from '@portfolio-pm/smart-add';
import { startOfDayLocal } from '../task-view-dates.ts';
import type { TaskWorkspaceView } from '../task-workspace-model.ts';
import {
  type BulkEditDraft,
  DEFAULT_TAG_COLORS,
  type DateToolbarField,
  MOBILE_TASK_VIEW_QUERY,
  POSTPONE_OPTIONS,
  type PostponeDelayUnit,
  type PostponeOptionId,
  type TaskPatch,
} from './options.ts';

/**
 * Pure helpers behind the Tasks workspace toolbar and list actions:
 * date/postpone patch builders, tag utilities, selection predicates,
 * and small formatting functions. Extracted from TaskWorkspace.tsx.
 */

export function matchesMobileTaskView(): boolean {
  return (
    typeof window.matchMedia === 'function' && window.matchMedia(MOBILE_TASK_VIEW_QUERY).matches
  );
}

export function triggerHapticClick(): void {
  if (typeof navigator.vibrate !== 'function') return;
  try {
    navigator.vibrate(10);
  } catch {
    // Haptics are optional; the visual threshold remains authoritative.
  }
}

export function getTaskSummaryQuery(view: TaskWorkspaceView, viewLabel: string): string {
  if (view.kind === 'project') return `project:"${viewLabel}"`;
  if (view.kind === 'tag') return `tag:${view.tag}`;
  if (view.kind === 'smart') return '';

  switch (view.id) {
    case 'all':
      return 'status:open';
    case 'today':
      return '(due:today OR scheduled:today)';
    case 'tomorrow':
      return '(due:tomorrow OR scheduled:tomorrow)';
    case 'week':
      return '(due:this-week OR scheduled:this-week)';
    case 'favorites':
      return 'favourite:true';
    case 'trash':
      return 'trashed:true';
  }
}

export function clampNumber(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, Math.round(value)));
}

export function localDateInputToTimestamp(value: string): number | undefined {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return undefined;
  return new Date(year, month - 1, day).getTime();
}

export function buildDatePatch(field: DateToolbarField, value: string): TaskPatch {
  const timestamp = localDateInputToTimestamp(value);
  if (field === 'dueAt') {
    return timestamp === undefined
      ? { dueAt: undefined, dueAtHasTime: undefined }
      : { dueAt: timestamp, dueAtHasTime: false };
  }
  return timestamp === undefined
    ? { scheduledAt: undefined, scheduledAtHasTime: undefined }
    : { scheduledAt: timestamp, scheduledAtHasTime: false };
}

export function buildPostponePatch(task: Task, optionId: PostponeOptionId, now: number): TaskPatch {
  const option = POSTPONE_OPTIONS.find((candidate) => candidate.id === optionId);
  return buildPostponeDelayPatch(task, option?.delay ?? { amount: 1, unit: 'days' }, now);
}

export function buildShortcutPostponePatch(task: Task, now: number): TaskPatch {
  const patch: TaskPatch = {};
  const today = startOfDayLocal(now);

  if (task.dueAt !== undefined && startOfDayLocal(task.dueAt) < today) {
    patch.dueAt = moveTimestampToDay(task.dueAt, today);
    patch.dueAtHasTime = task.dueAtHasTime ?? true;
  }
  if (task.scheduledAt !== undefined && startOfDayLocal(task.scheduledAt) < today) {
    patch.scheduledAt = moveTimestampToDay(task.scheduledAt, today);
    patch.scheduledAtHasTime = task.scheduledAtHasTime ?? true;
  }

  if (Object.keys(patch).length > 0) return patch;
  return buildPostponePatch(task, '1d', now);
}

export function buildPostponeDelayPatch(
  task: Task,
  delay: { amount: number; unit: PostponeDelayUnit },
  now: number,
): TaskPatch {
  const patch: TaskPatch = {};
  if (task.dueAt !== undefined) {
    patch.dueAt = postponeTimestamp(task.dueAt, delay);
    patch.dueAtHasTime = task.dueAtHasTime ?? true;
  }
  if (task.scheduledAt !== undefined) {
    patch.scheduledAt = postponeTimestamp(task.scheduledAt, delay);
    patch.scheduledAtHasTime = task.scheduledAtHasTime ?? true;
  }
  if (task.dueAt === undefined && task.scheduledAt === undefined) {
    patch.scheduledAt = postponeTimestamp(startOfDayLocal(now), delay);
    patch.scheduledAtHasTime = false;
  }
  return patch;
}

export function buildSmartPostponePatch(task: Task, value: string, now: number): TaskPatch {
  const target = resolveSmartDateTimestamp(value, now);
  if (target === undefined) return {};

  const today = startOfDayLocal(now);
  const targetDay = startOfDayLocal(target);
  const dayOffset = Math.round((targetDay - today) / (24 * 60 * 60 * 1000));
  const patch: TaskPatch = {};

  if (task.dueAt !== undefined) {
    patch.dueAt = shiftTimestampByDays(task.dueAt, dayOffset);
    patch.dueAtHasTime = task.dueAtHasTime ?? true;
  }
  if (task.scheduledAt !== undefined) {
    patch.scheduledAt = shiftTimestampByDays(task.scheduledAt, dayOffset);
    patch.scheduledAtHasTime = task.scheduledAtHasTime ?? true;
  }
  if (task.dueAt === undefined && task.scheduledAt === undefined) {
    patch.scheduledAt = targetDay;
    patch.scheduledAtHasTime = false;
  }
  return patch;
}

export function postponeTimestamp(
  timestamp: number,
  delay: { amount: number; unit: PostponeDelayUnit },
): number {
  const date = new Date(timestamp);
  switch (delay.unit) {
    case 'days':
      date.setDate(date.getDate() + delay.amount);
      break;
    case 'weeks':
      date.setDate(date.getDate() + delay.amount * 7);
      break;
    case 'months':
      date.setMonth(date.getMonth() + delay.amount);
      break;
    case 'years':
      date.setFullYear(date.getFullYear() + delay.amount);
      break;
  }
  return date.getTime();
}

export function shiftTimestampByDays(timestamp: number, days: number): number {
  const date = new Date(timestamp);
  date.setDate(date.getDate() + days);
  return date.getTime();
}

export function moveTimestampToDay(timestamp: number, targetDay: number): number {
  const date = new Date(startOfDayLocal(targetDay));
  const sourceDate = new Date(timestamp);
  date.setHours(
    sourceDate.getHours(),
    sourceDate.getMinutes(),
    sourceDate.getSeconds(),
    sourceDate.getMilliseconds(),
  );
  return date.getTime();
}

export function isTaskOverdueForSelection(task: Task, now: number): boolean {
  const today = startOfDayLocal(now);
  return [task.dueAt, task.scheduledAt].some(
    (timestamp) => timestamp !== undefined && startOfDayLocal(timestamp) < today,
  );
}

export function isTaskDueOnOffset(task: Task, now: number, offset: number): boolean {
  if (task.dueAt === undefined) return false;
  const date = new Date(startOfDayLocal(now));
  date.setDate(date.getDate() + offset);
  return startOfDayLocal(task.dueAt) === date.getTime();
}

export function normalizeTag(tag: string): string {
  return tag.trim().replace(/^#/, '');
}

export function uniqueTags(tags: string[]): string[] {
  return [...new Set(tags.filter(Boolean))];
}

export function resolveSmartDateTimestamp(value: string, now: number): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  const relativeDate = dateFromRelativeDateText(trimmed, now);
  if (relativeDate) return relativeDate.getTime();

  const parsed = parseSmartAdd(`Task ^${trimmed}`, { now: new Date(now) });
  if ('error' in parsed) return undefined;
  return parsed.dueAt;
}

export function dateFromRelativeDateText(value: string, now: number): Date | undefined {
  const match = /^(\d+)\s*(day|days|week|weeks|month|months|year|years)$/i.exec(value.trim());
  if (!match) return undefined;

  const amount = Number(match[1]);
  const unit = match[2]?.toLowerCase();
  if (!Number.isInteger(amount) || amount <= 0 || unit === undefined) return undefined;

  const date = new Date(startOfDayLocal(now));
  if (unit.startsWith('day')) date.setDate(date.getDate() + amount);
  if (unit.startsWith('week')) date.setDate(date.getDate() + amount * 7);
  if (unit.startsWith('month')) date.setMonth(date.getMonth() + amount);
  if (unit.startsWith('year')) date.setFullYear(date.getFullYear() + amount);
  return date;
}

export function tagsFromSearchText(value: string): string[] {
  return uniqueTags(
    value
      .split(',')
      .map((tag) => normalizeTag(tag))
      .filter(Boolean),
  );
}

export function getTagColor(tag: string, tagColors: TagColorMap): string {
  return tagColors[tag] ?? DEFAULT_TAG_COLORS[hashTag(tag)] ?? '#64748b';
}

export function hashTag(tag: string): number {
  let hash = 0;
  for (const char of tag) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return hash % DEFAULT_TAG_COLORS.length;
}

export function buildSmartDatePatch(
  field: DateToolbarField,
  value: string,
  now: number,
): TaskPatch {
  const trimmed = value.trim();
  if (!trimmed) return {};

  const parsed = parseSmartAdd(`Task ${field === 'dueAt' ? '^' : '~'}${trimmed}`, {
    now: new Date(now),
  });
  if ('error' in parsed) return {};

  if (field === 'dueAt') {
    if (parsed.dueAt === undefined) return buildRelativeDateTextPatch(field, trimmed, now);
    return { dueAt: parsed.dueAt, dueAtHasTime: parsed.dueAtHasTime ?? false };
  }

  if (parsed.scheduledAt === undefined) return buildRelativeDateTextPatch(field, trimmed, now);
  return {
    scheduledAt: parsed.scheduledAt,
    scheduledAtHasTime: parsed.scheduledAtHasTime ?? false,
  };
}

export function buildRelativeDateTextPatch(
  field: DateToolbarField,
  value: string,
  now: number,
): TaskPatch {
  const date = dateFromRelativeDateText(value, now);
  if (!date) return {};
  const inputValue = dateInputValueFromDate(date);
  return buildDatePatch(field, inputValue);
}

export function buildClearDatePatch(field: DateToolbarField): TaskPatch {
  return field === 'dueAt'
    ? { dueAt: undefined, dueAtHasTime: undefined }
    : { scheduledAt: undefined, scheduledAtHasTime: undefined };
}

export function buildDateQuickOptions(
  field: DateToolbarField,
  now: number,
): Array<{ id: string; label: string; patch: TaskPatch }> {
  const offsets = [
    { id: 'today', label: 'Today', days: 0 },
    { id: 'tomorrow', label: 'Tomorrow', days: 1 },
    ...[2, 3, 4].map((days) => ({
      id: `day-${days}`,
      label: formatWeekdayOffset(now, days),
      days,
    })),
    { id: '1w', label: '1 week', days: 7 },
    { id: '2w', label: '2 weeks', days: 14 },
  ];

  return [
    ...offsets.map((option) => ({
      id: option.id,
      label: option.label,
      patch: buildDatePatch(field, dateInputValueForOffset(now, option.days)),
    })),
    { id: 'none', label: 'No date', patch: buildClearDatePatch(field) },
  ];
}

export function dateInputValueForOffset(now: number, offset: number): string {
  const date = new Date(startOfDayLocal(now));
  date.setDate(date.getDate() + offset);
  return dateInputValueFromDate(date);
}

export function dateInputValueFromDate(date: Date): string {
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

const WEEKDAY_SHORT_FORMAT = new Intl.DateTimeFormat(undefined, { weekday: 'short' });

export function formatWeekdayOffset(now: number, offset: number): string {
  const date = new Date(startOfDayLocal(now));
  date.setDate(date.getDate() + offset);
  return WEEKDAY_SHORT_FORMAT.format(date);
}

export function buildBulkEditPatch(draft: BulkEditDraft): TaskPatch {
  const patch: TaskPatch = {};
  const owner = draft.owner.trim();
  const url = draft.url.trim();
  const location = draft.location.trim();
  const recurrence = draft.recurrence.trim();
  const notes = draft.notes.trim();
  const estimateMinutes = Number(draft.estimateMinutes);
  const reminderAt = draft.reminderAt ? Date.parse(draft.reminderAt) : Number.NaN;

  if (owner) patch.owner = owner;
  if (url) patch.url = url;
  if (location) patch.location = location;
  if (recurrence) {
    const normalized = recurrence.toLowerCase();
    if (normalized === 'never' || normalized === 'none' || normalized === 'clear') {
      patch.recurrence = undefined;
    } else {
      const parsed = parseRecurrence(recurrence);
      if (parsed) {
        patch.recurrence = {
          rrule: parsed.rrule,
          fromCompletion: parsed.fromCompletion,
        };
      }
    }
  }
  if (notes) patch.notes = notes;
  if (Number.isInteger(estimateMinutes) && estimateMinutes > 0)
    patch.estimateMinutes = estimateMinutes;
  if (!Number.isNaN(reminderAt)) patch.reminderAt = reminderAt;
  if (draft.energy) patch.energy = draft.energy;
  if (draft.daypart) patch.daypart = draft.daypart;

  return patch;
}

const SHORT_DATE_FORMAT = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

export function formatShortDate(value: number): string {
  return SHORT_DATE_FORMAT.format(new Date(value));
}

const INTERVAL_PREVIEW_FORMAT = new Intl.DateTimeFormat(undefined, {
  weekday: 'long',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

export function formatIntervalPreviewDate(value: number): string {
  return INTERVAL_PREVIEW_FORMAT.format(new Date(value));
}

export function parsePositiveInteger(value: string): number | undefined {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed <= 0) return undefined;
  return parsed;
}

export function formatMinutes(value: number): string {
  if (value < 60) return `${value} min`;
  const hours = Math.floor(value / 60);
  const minutes = value % 60;
  return minutes === 0 ? `${hours} hr` : `${hours} hr ${minutes} min`;
}
